import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  activateStockSyncRequest,
  getCatalogSyncRequest,
  getListingsRequest,
  getStockPreviewRequest,
  getStockSyncChannelsRequest,
  getStockSyncRequest,
  getStoreLocationsRequest,
  getStoreSalesRequest,
  startCatalogSyncRequest,
  startStockPreviewRequest,
  syncListingRequest,
  updateListingRequest,
  updateStockSyncRequest,
} from './stockSyncApi';
import { CHANNELS_PAGE_SIZE, LISTING_FILTER, MAX_CHANNEL_PAGES, STORE_SALE_FILTER } from './stockSyncConstants';
import { extractApiError, extractApiErrorDetail } from '../../utils/errors';

/*
 * Every thunk that acts on one store takes `{settingId}`. The reads also take
 * `background: true` when a watch asks them (`stockSyncListeners`): those do
 * not light the activity bar, nor the table's spinner.
 */

/** A run's report, or `null`: with none, the backend answers an empty body. */
const reportOf = (data) => (data && typeof data === 'object' && data.status ? data : null);

/**
 * The Shopify stores in reach, with how their sync stands. A warehouse narrows
 * them to the ones whose stock it holds.
 *
 * @param {{warehouseId?: number|null}} [params]
 * @returns {Promise<Array<object>>}
 */
export const fetchStockSyncChannels = createAsyncThunk(
  'stockSync/fetchChannels',
  async ({ warehouseId = null } = {}, { rejectWithValue }) => {
    try {
      const channels = [];
      for (let page = 1; page <= MAX_CHANNEL_PAGES; page += 1) {
        const params = { page, page_size: CHANNELS_PAGE_SIZE };
        if (warehouseId) params.warehouse = warehouseId;
        const { data } = await getStockSyncChannelsRequest(params);
        channels.push(...(data?.results || []));
        if (!data?.next) break;
      }
      return channels;
    } catch (error) {
      console.error('Error fetching Shopify stores:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener las tiendas'));
    }
  }
);

/** How one store's sync stands: settings, permissions, counts and last runs. */
export const fetchStockSync = createAsyncThunk(
  'stockSync/fetchStockSync',
  async ({ settingId }, { rejectWithValue }) => {
    try {
      const { data } = await getStockSyncRequest(settingId);
      return data;
    } catch (error) {
      console.error('Error fetching stock sync:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo leer la sincronización de la tienda'));
    }
  }
);

/**
 * Changes one setting of the store's sync. The setting is the pending key, so
 * the switch spins alone while the location is saved, and the other way round.
 * A refusal comes back with the field it belongs to.
 *
 * @param {{settingId: number, changes: {enabled?: boolean, location_id?: string}}} params
 * @returns {Promise<object>} The store's sync as it stands after the change.
 */
export const updateStockSync = createAsyncThunk(
  'stockSync/updateStockSync',
  async ({ settingId, changes }, { rejectWithValue }) => {
    try {
      const { data } = await updateStockSyncRequest(settingId, changes);
      return data;
    } catch (error) {
      console.error('Error updating stock sync:', error);
      return rejectWithValue(extractApiErrorDetail(error, 'No se pudo guardar el cambio'));
    }
  },
  { getPendingMeta: ({ arg }) => ({ pendingKey: Object.keys(arg.changes)[0] }) }
);

/** The store's locations, from Shopify itself. */
export const fetchStoreLocations = createAsyncThunk(
  'stockSync/fetchLocations',
  async ({ settingId }, { rejectWithValue }) => {
    try {
      const { data } = await getStoreLocationsRequest(settingId);
      return data?.results || [];
    } catch (error) {
      console.error('Error fetching store locations:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron leer las ubicaciones de la tienda'));
    }
  }
);

/** Brings the store's SKUs into the catalog. Resolves with the queued report. */
export const startCatalogSync = createAsyncThunk(
  'stockSync/startCatalogSync',
  async ({ settingId }, { rejectWithValue }) => {
    try {
      const { data } = await startCatalogSyncRequest(settingId);
      return data;
    } catch (error) {
      console.error('Error starting catalog sync:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo iniciar la sincronización de SKUs'));
    }
  }
);

export const fetchCatalogRun = createAsyncThunk(
  'stockSync/fetchCatalogRun',
  async ({ settingId }, { rejectWithValue }) => {
    try {
      const { data } = await getCatalogSyncRequest(settingId);
      return reportOf(data);
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo leer la sincronización de SKUs'));
    }
  }
);

/** Compares what the store has with what Ninja will publish, without changing either. Resolves with the queued report. */
export const startStockPreview = createAsyncThunk(
  'stockSync/startStockPreview',
  async ({ settingId }, { rejectWithValue }) => {
    try {
      const { data } = await startStockPreviewRequest(settingId);
      return data;
    } catch (error) {
      console.error('Error starting stock comparison:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo comparar con la tienda'));
    }
  }
);

/** The last comparison, with every row once it is done. */
export const fetchStockPreview = createAsyncThunk(
  'stockSync/fetchStockPreview',
  async ({ settingId }, { rejectWithValue }) => {
    try {
      const { data } = await getStockPreviewRequest(settingId);
      return reportOf(data);
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo leer la comparación con la tienda'));
    }
  }
);

/**
 * Confirms the comparison in use: from then on Ninja's number rules the store.
 * Resolves with the store's sync, its publication already queued.
 *
 * @param {{settingId: number, token: string}} params
 */
export const activateStockSync = createAsyncThunk(
  'stockSync/activateStockSync',
  async ({ settingId, token }, { rejectWithValue }) => {
    try {
      const { data } = await activateStockSyncRequest(settingId, { preview_id: token });
      return data;
    } catch (error) {
      console.error('Error activating stock sync:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo activar la publicación'));
    }
  }
);

/**
 * The page of linked products the filters describe, of the store in use.
 *
 * @param {{background?: boolean}} [params]
 * @returns {Promise<{settingId: number, count: number, results: Array<object>}>}
 */
export const fetchListings = createAsyncThunk(
  'stockSync/fetchListings',
  async (_ = {}, { getState, rejectWithValue }) => {
    const { selectedId, listings } = getState().stockSync;
    try {
      const { search, status, page, rowsPerPage } = listings.filters;
      const params = { page, page_size: rowsPerPage };
      if (search) params.search = search;
      if (status !== LISTING_FILTER.ALL) params.status = status;

      const { data } = await getListingsRequest(selectedId, params);
      return { settingId: selectedId, count: data?.count || 0, results: data?.results || [] };
    } catch (error) {
      console.error('Error fetching linked products:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los productos de la tienda'));
    }
  },
  { condition: (_, { getState }) => Boolean(getState().stockSync.selectedId) }
);

/**
 * The page of the store's own sales the filters describe, of the store in use.
 *
 * @returns {Promise<{settingId: number, count: number, results: Array<object>}>}
 */
export const fetchStoreSales = createAsyncThunk(
  'stockSync/fetchStoreSales',
  async (_ = {}, { getState, rejectWithValue }) => {
    const { selectedId, storeSales } = getState().stockSync;
    try {
      const { search, status, page, rowsPerPage } = storeSales.filters;
      const params = { page, page_size: rowsPerPage };
      if (search) params.search = search;
      if (status !== STORE_SALE_FILTER.ALL) params.status = status;

      const { data } = await getStoreSalesRequest(selectedId, params);
      return { settingId: selectedId, count: data?.count || 0, results: data?.results || [] };
    } catch (error) {
      console.error('Error fetching store sales:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener las ventas de la tienda'));
    }
  },
  { condition: (_, { getState }) => Boolean(getState().stockSync.selectedId) }
);

const listingPendingMeta = { getPendingMeta: ({ arg }) => ({ pendingKey: arg.listingId }) };

/** Pauses one product, or resumes it: resumed, it is pushed. */
export const updateListing = createAsyncThunk(
  'stockSync/updateListing',
  async ({ settingId, listingId, syncEnabled }, { rejectWithValue }) => {
    try {
      const { data } = await updateListingRequest(settingId, listingId, { sync_enabled: syncEnabled });
      return data;
    } catch (error) {
      console.error('Error updating linked product:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo cambiar el producto'));
    }
  },
  listingPendingMeta
);

/** Pushes one product to the store now. */
export const syncListing = createAsyncThunk(
  'stockSync/syncListing',
  async ({ settingId, listingId }, { rejectWithValue }) => {
    try {
      const { data } = await syncListingRequest(settingId, listingId);
      return data;
    } catch (error) {
      console.error('Error pushing linked product:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo publicar el producto'));
    }
  },
  listingPendingMeta
);
